import { QuestionCircleOutlined, InfoCircleFilled } from '@ant-design/icons';
import { Button, Checkbox, Col, Divider, Form, Input, Row, Select, Tooltip } from 'antd';
import Title from 'antd/es/typography/Title';
import React, { useEffect } from 'react';
import { FormDataType, ShortcutRecordData } from '@src/models/shortLink';
import { fixedSourceList } from '@src/constants/shortLink';
import { Group } from 'antd/lib/radio';
import styled from '@emotion/styled';
import MarketingInfo from './MarketingInfo';

const sourceOptions = [
  { label: 'Youtube', value: 'youtube' },
  { label: 'Facebook', value: 'meta' },
  { label: 'Instagram', value: 'instagram' },
  { label: 'Twitter', value: 'twitter' },
  { label: 'Kakao', value: 'kakao' },
  { label: 'Notion', value: 'notion' },
  { label: 'Realworld', value: 'realworld' },
  { label: '직접 입력', value: 'etc' }
];

interface InternalFormProps {
  isModify?: boolean;
  isCopy?: boolean;
  prevData?: ShortcutRecordData;
  onFinish: (value: FormDataType) => unknown;
}

export default function InternalForm({ onFinish, prevData, isModify, isCopy }: InternalFormProps) {
  const { id, redirectTo, note } = prevData || {
    id: '',
    redirectTo: '',
    note: ''
  };

  const params = new URLSearchParams(redirectTo.split('?').slice(1).join('?'));

  const originalUrl = redirectTo.slice(redirectTo.indexOf('deeplink_path') + 14, redirectTo.indexOf('&utm_source'));
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');
  const utmContent = params.get('utm_content');
  const flow = params.get('flow');
  const useAnonymous = params.get('useAnonymous');
  const collectBirth = params.get('collectBirth');
  const collectGender = params.get('collectGender');
  const notificationConfirm = decodeURIComponent(originalUrl).includes('notification_confirm=true');

  const [selectedValue, setSelectedValue] = React.useState(() => {
    if (isModify) {
      return fixedSourceList.includes(utmSource || '') ? utmSource : 'etc';
    }
    return '';
  });
  const [isUseAnonymous, setIsUseAnonymous] = React.useState(
    useAnonymous === 'true' || useAnonymous === 'false' ? useAnonymous === 'true' : null
  );

  function getInitialValues() {
    if (isModify) {
      return {
        originalUrl: decodeURIComponent(originalUrl || '')
          .replace('?notification_confirm=true', '')
          .replace('&notification_confirm=true', ''),
        shortUrl: id,
        memo: note,
        utmSource: fixedSourceList.includes(utmSource || '') ? utmSource : 'etc',
        utmSourceEtc: fixedSourceList.includes(utmSource || '') ? '' : utmSource || '',
        utmMedium: utmMedium || '',
        utmCampaign: utmCampaign || '',
        utmContent: utmContent || '',
        flow: flow !== '0',
        notificationConfirm,
        useAnonymous: useAnonymous === 'true' || useAnonymous === 'false' ? useAnonymous === 'true' : false,
        collectBirth: collectBirth === 'true' || collectBirth === 'false' ? collectBirth === 'true' : true,
        collectGender: collectGender === 'true' || collectGender === 'false' ? collectGender === 'true' : true
      };
    }
    if (isCopy) {
      return {
        originalUrl: decodeURIComponent(originalUrl || '')
          .replace('?notification_confirm=true', '')
          .replace('&notification_confirm=true', ''),
        memo: note,
        utmSource: fixedSourceList.includes(utmSource || '') ? utmSource : 'etc',
        utmSourceEtc: fixedSourceList.includes(utmSource || '') ? '' : utmSource || '',
        utmMedium: utmMedium || '',
        utmCampaign: utmCampaign || '',
        utmContent: utmContent || '',
        flow: flow !== '0',
        notificationConfirm,
        useAnonymous: useAnonymous === 'true' || useAnonymous === 'false' ? useAnonymous === 'true' : false,
        collectBirth: collectBirth === 'true' || collectBirth === 'false' ? collectBirth === 'true' : true,
        collectGender: collectGender === 'true' || collectGender === 'false' ? collectGender === 'true' : true
      };
    }
    return {
      collectBirth: true,
      collectGender: true
    };
  }

  const [form] = Form.useForm();

  useEffect(() => {
    if (isModify || isCopy) {
      setIsUseAnonymous(useAnonymous === 'true' || useAnonymous === 'false' ? useAnonymous === 'true' : false);
    }
  }, [isModify, isCopy, useAnonymous]);

  return (
    <Form
      form={form}
      initialValues={getInitialValues()}
      onFinish={onFinish}
      style={{ textAlign: 'left' }}
      wrapperCol={{ span: 24 }}
      labelCol={{ span: 24 }}
      validateMessages={{ required: '필수 항목입니다.' }}
    >
      <Form.Item
        name="originalUrl"
        label={<Title level={5}>앱 내 연결할 페이지 주소</Title>}
        required
        rules={[{ required: true }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="shortUrl"
        label={
          <Title level={5}>
            희망 단축 주소{' '}
            <span style={{ color: '#6c757d', fontSize: '0.8em', fontWeight: 'normal' }}>
              공백 시 자동 생성, 한 번 생성 시 변경 불가
            </span>
          </Title>
        }
      >
        <Input disabled={isModify} />
      </Form.Item>
      <Form.Item name="memo" label={<Title level={5}>메모</Title>}>
        <Input />
      </Form.Item>
      <Title level={5}>
        마케팅 관련 설정{' '}
        <Tooltip placement="top" title={<MarketingInfo />}>
          <QuestionCircleOutlined />
        </Tooltip>
      </Title>
      <Row gutter={[16, 0]}>
        <Col span={24}>
          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item label="UTM Source" name="utmSource" wrapperCol={undefined} labelCol={{ span: 'none' }}>
                <Select options={sourceOptions} onChange={(value) => setSelectedValue(value)} />
              </Form.Item>
            </Col>
            {selectedValue === 'etc' && (
              <Col span={12}>
                <Form.Item label="직접 입력" name="utmSourceEtc" wrapperCol={undefined} labelCol={{ span: 'none' }}>
                  <Input />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Col>
        <Col flex={1}>
          <Form.Item label="UTM Campaign" name="utmCampaign" wrapperCol={undefined} labelCol={{ span: 'none' }}>
            <Input />
          </Form.Item>
        </Col>
        <Col flex={1}>
          <Form.Item label="UTM Medium" name="utmMedium" wrapperCol={undefined} labelCol={{ span: 'none' }}>
            <Input />
          </Form.Item>
        </Col>
        <Col flex={1}>
          <Form.Item label="UTM Content" name="utmContent" wrapperCol={undefined} labelCol={{ span: 'none' }}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <Form.Item
        name="notificationConfirm"
        label={
          <>
            알림 설정 확인 여부&nbsp;
            <Tooltip title="체크할 경우 앱 이동 시 알림 설정 권한 팝업이 뜨게 됩니다.">
              <QuestionCircleOutlined />
            </Tooltip>
          </>
        }
        valuePropName="checked"
      >
        <Checkbox>필요</Checkbox>
      </Form.Item>
      <Form.Item
        name="useAnonymous"
        label="익명 계정 사용 여부"
        required
        rules={[{ required: true, message: '필수 항목입니다.' }]}
      >
        <Group
          onChange={(e) => setIsUseAnonymous(e.target.value)}
          disabled={isModify}
          options={[
            { label: '사용', value: true },
            { label: '사용 안함', value: false }
          ]}
        />
      </Form.Item>
      {isUseAnonymous === false && (
        <>
          <Form.Item
            name="collectBirth"
            label="회원가입 시 생년월일 항목 수집 여부"
            required
            rules={[{ required: true }]}
          >
            <Group
              options={[
                { label: '수집', value: true },
                { label: '수집 안함', value: false }
              ]}
            />
          </Form.Item>
          <Form.Item name="collectGender" label="회원가입 시 성별 항목 수집 여부" required rules={[{ required: true }]}>
            <Group
              options={[
                { label: '수집', value: true },
                { label: '수집 안함', value: false }
              ]}
            />
          </Form.Item>
          <Form.Item name="flow" label="로그인 화면 우선 노출 여부" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox>필요</Checkbox>
          </Form.Item>
          <NoticeBox>
            <div style={{ alignSelf: 'flex-start', color: '#3C78F4' }}>
              <InfoCircleFilled />
            </div>
            체크 시, 단축 링크로 접근할 때 로그인 화면이 먼저 노출되어 선 가입 후 플레이를 유도할 수 있습니다. 콘텐츠
            화면을 먼저 보여주고 싶다면 체크를 해제해 주세요.
          </NoticeBox>
        </>
      )}
      <div style={{ textAlign: 'right' }}>
        <Button htmlType="submit" type="primary">
          {isModify ? '수정하기' : '추가하기'}
        </Button>
      </div>
    </Form>
  );
}

const NoticeBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #e6f4ff;
  padding: 10px;
  margin-bottom: 16px;
  border-radius: 5px;
  word-break: keep-all;
`;
